import { useRef, useEffect, useCallback } from "react";

export default function MyceliumCanvas() {
  const canvasRef = useRef(null);
  const stateRef = useRef({ nodes: [], edges: [], particles: [], tick: 0 });

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = (canvas.width = window.innerWidth);
    const H = (canvas.height = window.innerHeight);
    const N = Math.min(Math.floor((W * H) / 18000), 55);

    const nodes = Array.from({ length: N }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      r: 1.5 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
      speed: 0.008 + Math.random() * 0.012,
    }));

    const edges = [];
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        if (Math.sqrt(dx * dx + dy * dy) < 180) edges.push([i, j]);
      }
    }

    const particles = edges.slice(0, Math.floor(edges.length * 0.35)).map((edge) => ({
      edge,
      t: Math.random(),
      dir: Math.random() > 0.5 ? 1 : -1,
      speed: 0.001 + Math.random() * 0.002,
    }));

    stateRef.current = { nodes, edges, particles, tick: 0, ctx, W, H };
  }, []);

  useEffect(() => {
    init();
    let raf;

    function draw() {
      const { nodes, edges, particles, ctx, W, H } = stateRef.current;
      if (!ctx) return;
      stateRef.current.tick++;
      const tick = stateRef.current.tick;

      ctx.clearRect(0, 0, W, H);

      // Drift nodes
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0) n.x = W;
        if (n.x > W) n.x = 0;
        if (n.y < 0) n.y = H;
        if (n.y > H) n.y = 0;
      }

      // Edges
      for (const [i, j] of edges) {
        const a = nodes[i], b = nodes[j];
        const dx = a.x - b.x, dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const alpha = Math.max(0, 1 - dist / 200) * 0.35;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(29,158,117,${alpha})`;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }

      // Nodes
      for (const n of nodes) {
        const pulse = 1 + 0.25 * Math.sin(tick * n.speed + n.phase);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(29,158,117,${0.35 + 0.2 * pulse})`;
        ctx.fill();
      }

      // Particles
      for (const p of particles) {
        p.t += p.dir * p.speed;
        if (p.t > 1 || p.t < 0) {
          p.dir *= -1;
          p.t = Math.max(0, Math.min(1, p.t));
          if (Math.random() > 0.7 && edges.length > 1) {
            p.edge = edges[Math.floor(Math.random() * edges.length)];
          }
        }
        const a = nodes[p.edge[0]], b = nodes[p.edge[1]];
        const px = a.x + (b.x - a.x) * p.t;
        const py = a.y + (b.y - a.y) * p.t;
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(239,159,39,0.55)";
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    }

    draw();

    const onResize = () => init();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="mycelium-canvas"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity: 0.55,
      }}
    />
  );
}
